(function () {
  const NAME = 'quick-order-mayoristas';

  class QuickOrderMayoristas extends window.HTMLElement {
    connectedCallback() {
      this.tierEls = Array.from(this.querySelectorAll('[data-qom-tier]'));
      this.rowEls = Array.from(this.querySelectorAll('[data-qom-row]'));
      this.checkoutUrl = this.dataset.checkoutUrl || '/cart';

      const progressEl = this.querySelector('[data-qom-progress]');
      this.maxTierMin = progressEl ? parseFloat(progressEl.dataset.maxTier) || 0 : 0;

      this.tiers = this.tierEls
        .map((el) => ({
          el,
          min: parseFloat(el.dataset.minSubtotal) || 0,
          discountPercent: parseFloat(el.dataset.discountPercent) || 0,
          shippingCost: parseFloat(el.dataset.shippingCost) || 0,
        }))
        .sort((a, b) => a.min - b.min);

      this.generalShippingTiers = {
        tier1Max: parseFloat(this.dataset.generalTier1Max) || 0,
        tier1Cost: parseFloat(this.dataset.generalTier1Cost) || 0,
        tier2Max: parseFloat(this.dataset.generalTier2Max) || 0,
        tier2Cost: parseFloat(this.dataset.generalTier2Cost) || 0,
        tier3Max: parseFloat(this.dataset.generalTier3Max) || 0,
        tier3Cost: parseFloat(this.dataset.generalTier3Cost) || 0,
        tier4Min: parseFloat(this.dataset.generalTier4Min) || 0,
        tier4Cost: parseFloat(this.dataset.generalTier4Cost) || 0,
        tier5Min: parseFloat(this.dataset.generalTier5Min) || 0,
        tier5Cost: parseFloat(this.dataset.generalTier5Cost) || 0,
        tier6Min: parseFloat(this.dataset.generalTier6Min) || 0,
        tier6Cost: parseFloat(this.dataset.generalTier6Cost) || 0,
      };

      this.expandedGroups = new Set();
      this._syncTimer = null;
      this._syncing = false;

      this.querySelectorAll('[data-qom-qty-input]').forEach((el) => this.updateZeroStyle(el));

      this.bindEvents();
      this.hydrateFromCart().finally(() => {
        this.recalculate();
      });

      this._onKeydown = (e) => {
        if (e.key === 'Escape') this.closeDrawer();
      };
      document.addEventListener('keydown', this._onKeydown);

      this._onScrollOrResize = () => {
        if (this._stickyRaf) return;
        this._stickyRaf = requestAnimationFrame(() => {
          this._stickyRaf = null;
          this.updateStickyBarPosition();
        });
      };
      window.addEventListener('scroll', this._onScrollOrResize, { passive: true });
      window.addEventListener('resize', this._onScrollOrResize);
      this.updateStickyBarPosition();

      const stickyBar = this.querySelector('[data-qom-sticky-progress]');
      if (progressEl && stickyBar && 'IntersectionObserver' in window) {
        this._progressObserver = new IntersectionObserver((entries) => {
          const entry = entries[0];
          const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          stickyBar.classList.toggle('is-visible', scrolledPast);
        });
        this._progressObserver.observe(progressEl);
      }
    }

    disconnectedCallback() {
      if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
      if (this._onScrollOrResize) {
        window.removeEventListener('scroll', this._onScrollOrResize);
        window.removeEventListener('resize', this._onScrollOrResize);
      }
      if (this._progressObserver) this._progressObserver.disconnect();
    }

    updateStickyBarPosition() {
      const stickyBar = this.querySelector('[data-qom-sticky-progress]');
      if (!stickyBar) return;
      const header = document.getElementById('shopify-section-header');
      const top = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      stickyBar.style.top = top + 'px';
    }

    updateZeroStyle(input) {
      if (input.value === '0') {
        input.classList.add('is-zero');
      } else {
        input.classList.remove('is-zero');
      }
    }

    async hydrateFromCart() {
      try {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const cart = await response.json();
        const qtyByVariant = {};
        (cart.items || []).forEach((item) => {
          const id = String(item.variant_id);
          qtyByVariant[id] = (qtyByVariant[id] || 0) + item.quantity;
        });
        this.querySelectorAll('[data-qom-stepper]').forEach((stepper) => {
          const variantId = stepper.dataset.variantId;
          const qty = qtyByVariant[variantId];
          if (qty) {
            const input = stepper.querySelector('[data-qom-qty-input]');
            if (input) {
              input.value = qty;
              this.updateZeroStyle(input);
            }
          }
        });
      } catch (err) {
        console.error('[quick-order-mayoristas]', err);
      }
    }

    openDrawer() {
      const summary = this.querySelector('[data-qom-summary]');
      const backdrop = this.querySelector('[data-qom-drawer-backdrop]');
      const toggle = this.querySelector('[data-qom-mobile-bar-toggle]');
      if (summary) summary.classList.add('is-open');
      if (backdrop) backdrop.classList.add('is-open');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      document.body.classList.add('qom-drawer-open');
    }

    closeDrawer() {
      const summary = this.querySelector('[data-qom-summary]');
      const backdrop = this.querySelector('[data-qom-drawer-backdrop]');
      const toggle = this.querySelector('[data-qom-mobile-bar-toggle]');
      if (summary) summary.classList.remove('is-open');
      if (backdrop) backdrop.classList.remove('is-open');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      document.body.classList.remove('qom-drawer-open');
    }

    nudgeByVariantId(variantId, delta) {
      if (!variantId) return;
      const realStepper = this.querySelector('[data-qom-stepper][data-variant-id="' + variantId + '"]');
      if (!realStepper) return;
      const input = realStepper.querySelector('[data-qom-qty-input]');
      if (!input) return;
      input.value = Math.max(0, (parseInt(input.value, 10) || 0) + delta);
      this.updateZeroStyle(input);
      this.recalculate();
    }

    bindEvents() {
      this.addEventListener('click', (e) => {
        const notesTrigger = e.target.closest('[data-qom-notes-trigger]');
        if (notesTrigger) {
          const wrapper = notesTrigger.closest('[data-qom-notes-wrapper]');
          const tooltip = wrapper ? wrapper.querySelector('[data-qom-notes-tooltip]') : null;
          if (tooltip) {
            const wasOpen = tooltip.hasAttribute('data-open');
            this.querySelectorAll('[data-qom-notes-tooltip]').forEach((t) => {
              t.removeAttribute('data-open');
              t.classList.remove('right');
            });
            if (!wasOpen) {
              tooltip.setAttribute('data-open', '');
              const rect = tooltip.getBoundingClientRect();
              if (rect.right > window.innerWidth - 5) tooltip.classList.add('right');
            }
          }
          return;
        }
        if (!e.target.closest('[data-qom-notes-tooltip]')) {
          this.querySelectorAll('[data-qom-notes-tooltip]').forEach((t) => {
            t.removeAttribute('data-open');
            t.classList.remove('right');
          });
        }

        const incBtn = e.target.closest('[data-qom-increment]');
        const decBtn = e.target.closest('[data-qom-decrement]');
        const removeBtn = e.target.closest('[data-qom-remove-row]');
        const clearBtn = e.target.closest('[data-qom-clear-all]');
        const checkoutBtn = e.target.closest('[data-qom-checkout]');
        const groupHeader = e.target.closest('[data-qom-group-header]');
        const mobileBarToggle = e.target.closest('[data-qom-mobile-bar-toggle]');
        const drawerClose = e.target.closest('[data-qom-drawer-close]');
        const drawerBackdrop = e.target.closest('[data-qom-drawer-backdrop]');
        const summaryIncBtn = e.target.closest('[data-qom-summary-increment]');
        const summaryDecBtn = e.target.closest('[data-qom-summary-decrement]');

        if (incBtn) {
          const stepper = incBtn.closest('[data-qom-stepper]');
          const input = stepper.querySelector('[data-qom-qty-input]');
          input.value = (parseInt(input.value, 10) || 0) + 1;
          this.updateZeroStyle(input);
          this.recalculate();
        } else if (decBtn) {
          const stepper = decBtn.closest('[data-qom-stepper]');
          const input = stepper.querySelector('[data-qom-qty-input]');
          input.value = Math.max(0, (parseInt(input.value, 10) || 0) - 1);
          this.updateZeroStyle(input);
          this.recalculate();
        } else if (removeBtn) {
          const row = removeBtn.closest('[data-qom-row]');
          row.querySelectorAll('[data-qom-qty-input]').forEach((el) => {
            el.value = 0;
            this.updateZeroStyle(el);
          });
          this.recalculate();
        } else if (clearBtn) {
          this.rowEls.forEach((row) => {
            row.querySelectorAll('[data-qom-qty-input]').forEach((el) => {
              el.value = 0;
              this.updateZeroStyle(el);
            });
          });
          this.recalculate();
        } else if (checkoutBtn) {
          this.addAllToCart();
        } else if (groupHeader) {
          const group = groupHeader.closest('[data-qom-group]');
          const label = group.dataset.groupLabel;
          if (this.expandedGroups.has(label)) {
            this.expandedGroups.delete(label);
          } else {
            this.expandedGroups.add(label);
          }
          group.classList.toggle('is-collapsed');
        } else if (mobileBarToggle) {
          this.openDrawer();
        } else if (drawerClose) {
          this.closeDrawer();
        } else if (drawerBackdrop) {
          this.closeDrawer();
        } else if (summaryIncBtn) {
          this.nudgeByVariantId(summaryIncBtn.dataset.variantId, 1);
        } else if (summaryDecBtn) {
          this.nudgeByVariantId(summaryDecBtn.dataset.variantId, -1);
        }
      });

      this.addEventListener('focusin', (e) => {
        if (e.target.matches('[data-qom-qty-input]') && e.target.value === '0') {
          e.target.value = '';
        }
      });

      this.addEventListener('focusout', (e) => {
        if (e.target.matches('[data-qom-qty-input]')) {
          if (e.target.value.trim() === '') {
            e.target.value = '0';
          }
          this.updateZeroStyle(e.target);
        }
      });

      this.addEventListener('input', (e) => {
        if (e.target.matches('[data-qom-qty-input]')) {
          this.updateZeroStyle(e.target);
          this.recalculate();
        }
      });

      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-qom-qty-input]')) {
          const val = Math.max(0, parseInt(e.target.value, 10) || 0);
          e.target.value = val;
          this.updateZeroStyle(e.target);
          this.recalculate();
        }
      });
    }

    getActiveTier(subtotal) {
      let active = null;
      for (const tier of this.tiers) {
        if (subtotal >= tier.min) active = tier;
      }
      return active;
    }

    getNextTier(subtotal) {
      return this.tiers.find((tier) => subtotal < tier.min) || null;
    }

    getGeneralShipping(subtotal) {
      const g = this.generalShippingTiers;
      if (subtotal <= 0) return 0;
      if (subtotal < g.tier1Max) return g.tier1Cost;
      if (subtotal <= g.tier2Max) return g.tier2Cost;
      if (subtotal < g.tier3Max) return g.tier3Cost;
      if (g.tier6Min > 0 && subtotal >= g.tier6Min) return g.tier6Cost;
      if (g.tier5Min > 0 && subtotal >= g.tier5Min) return g.tier5Cost;
      if (g.tier4Min > 0 && subtotal >= g.tier4Min) return g.tier4Cost;
      return 0;
    }

    parsePrice(raw) {
      if (raw == null) return 0;
      const cleaned = String(raw).trim().replace(',', '.');
      const value = parseFloat(cleaned);
      return Number.isFinite(value) ? value / 100 : 0;
    }

    recalculate() {
      let subtotal = 0;
      let totalKg = 0;
      const lineItems = [];

      this.rowEls.forEach((row) => {
        let rowSubtotal = 0;
        row.querySelectorAll('[data-qom-stepper]').forEach((stepper) => {
          const qty = parseInt(stepper.querySelector('[data-qom-qty-input]').value, 10) || 0;
          const price = this.parsePrice(stepper.dataset.price);
          if (qty > 0) {
            rowSubtotal += qty * price;
            totalKg += qty;
            lineItems.push({
              variantId: stepper.dataset.variantId,
              quantity: qty,
              label: row.dataset.productTitle,
              profileLabel: row.dataset.profileLabel || row.dataset.productTitle,
              dotColor: row.dataset.dotColor,
              format: stepper.dataset.format,
              price,
              linePrice: qty * price,
            });
          }
        });
        subtotal += rowSubtotal;

        const totalOriginalEl = row.querySelector('[data-qom-row-total-original]');
        if (totalOriginalEl) totalOriginalEl.textContent = this.formatMoney(rowSubtotal);
      });

      const activeTier = this.getActiveTier(subtotal);
      const discountPercent = activeTier ? activeTier.discountPercent : 0;
      const shippingCost = activeTier ? activeTier.shippingCost : this.getGeneralShipping(subtotal);

      const discountAmount = subtotal * (discountPercent / 100);
      const subtotalWithDiscount = subtotal - discountAmount;
      const total = subtotalWithDiscount + shippingCost;

      this.rowEls.forEach((row) => {
        row.classList.toggle('has-discount', discountPercent > 0);
        const unitOriginalEl = row.querySelector('[data-qom-unit-original]');
        const unitDiscountedEl = row.querySelector('[data-qom-unit-discounted]');
        const unitPrice = this.parsePrice(row.dataset.unitPrice);
        if (unitOriginalEl) unitOriginalEl.textContent = this.formatMoney(unitPrice);
        if (unitDiscountedEl) {
          unitDiscountedEl.textContent = this.formatMoney(unitPrice * (1 - discountPercent / 100));
        }
        const originalTotalForRow = Array.from(row.querySelectorAll('[data-qom-stepper]')).reduce((sum, stepper) => {
          const qty = parseInt(stepper.querySelector('[data-qom-qty-input]').value, 10) || 0;
          const price = this.parsePrice(stepper.dataset.price);
          return sum + qty * price;
        }, 0);
        const discountedEl = row.querySelector('[data-qom-row-total-discounted]');
        if (discountedEl) discountedEl.textContent = this.formatMoney(originalTotalForRow * (1 - discountPercent / 100));
      });

      this.tiers.forEach((tier) => {
        tier.el.classList.toggle('is-active', tier === activeTier);
        const badge = tier.el.querySelector('[data-qom-tier-badge]');
        if (badge) badge.hidden = tier !== activeTier;
      });

      const progressEl = this.querySelector('[data-qom-progress]');
      if (progressEl && this.tiers.length) {
        const nextTier = this.getNextTier(subtotal);
        const fillEl = progressEl.querySelector('[data-qom-progress-fill]');
        const textEl = progressEl.querySelector('[data-qom-progress-text]');
        const goalEl = progressEl.querySelector('[data-qom-progress-goal]');
        const maxTierMin = this.maxTierMin || this.tiers[this.tiers.length - 1].min;

        const pct = maxTierMin > 0 ? Math.min(100, Math.max(0, (subtotal / maxTierMin) * 100)) : 0;
        fillEl.style.width = pct + '%';

        if (nextTier) {
          const missing = nextTier.min - subtotal;
          textEl.textContent = `Te faltan ${this.formatMoney(missing)} para llegar al siguiente tier`;
          goalEl.textContent = `(${nextTier.discountPercent}% de descuento)`;
        } else {
          textEl.textContent = '¡Llegaste al tier máximo de descuento!';
          goalEl.textContent = '';
        }

        const stickyFillEl = this.querySelector('[data-qom-sticky-progress-fill]');
        if (stickyFillEl) stickyFillEl.style.width = pct + '%';
        this.setText('[data-qom-sticky-progress-badge]', `${discountPercent}%`);
        if (nextTier) {
          const missing = nextTier.min - subtotal;
          this.setText(
            '[data-qom-sticky-progress-text]',
            `Faltan ${this.formatMoney(missing)} para ${nextTier.discountPercent}%`
          );
        } else if (this.tiers.length) {
          this.setText('[data-qom-sticky-progress-text]', '¡Tier máximo de descuento aplicado!');
        }
      }

      const linesEl = this.querySelector('[data-qom-summary-lines]');
      const emptyEl = this.querySelector('[data-qom-summary-empty]');
      if (linesEl) {
        linesEl.querySelectorAll('.qom-summary-line-group').forEach((el) => el.remove());
        if (lineItems.length === 0) {
          if (emptyEl) emptyEl.hidden = false;
        } else {
          if (emptyEl) emptyEl.hidden = true;
          const grouped = {};
          lineItems.forEach((item) => {
            grouped[item.label] = grouped[item.label] || { color: item.dotColor, profileLabel: item.profileLabel, items: [] };
            grouped[item.label].items.push(item);
          });
          Object.keys(grouped).forEach((label) => {
            const group = grouped[label];
            const groupTotal = group.items.reduce((s, i) => s + i.linePrice, 0);
            const groupTotalDiscounted = groupTotal * (1 - discountPercent / 100);
            const groupKg = group.items.reduce((s, i) => s + i.quantity, 0);
            const isCollapsed = !this.expandedGroups.has(label);

            const groupEl = document.createElement('div');
            groupEl.className = 'qom-summary-line-group' + (isCollapsed ? ' is-collapsed' : '');
            groupEl.setAttribute('data-qom-group', '');
            groupEl.dataset.groupLabel = label;

            const subRows = group.items
              .map((item) => {
                const formatLabel = item.format === 'grano' ? 'En grano (1 kg)' : 'Molido (1 kg)';
                return `<div class="qom-summary-line-item">
                  <span class="qom-summary-line-format">${formatLabel}</span>
                  <div class="qom-mini-stepper">
                    <button type="button" class="qom-stepper-btn" data-qom-summary-decrement data-variant-id="${item.variantId}" aria-label="Restar">&minus;</button>
                    <span class="qom-mini-stepper-value">${item.quantity}</span>
                    <button type="button" class="qom-stepper-btn" data-qom-summary-increment data-variant-id="${item.variantId}" aria-label="Sumar">+</button>
                  </div>
                  <span class="qom-summary-line-price">${this.formatMoney(item.linePrice)}</span>
                </div>`;
              })
              .join('');

            const profileText = group.profileLabel || label;
            const badgeLabel = String(profileText).trim().toLowerCase();

            const granoQty = group.items.filter((i) => i.format === 'grano').reduce((s, i) => s + i.quantity, 0);
            const molidoQty = group.items.filter((i) => i.format === 'molido').reduce((s, i) => s + i.quantity, 0);
            const parts = [];
            if (granoQty > 0) parts.push(`${granoQty} en grano`);
            if (molidoQty > 0) parts.push(`${molidoQty} molido`);
            const breakdown = parts.length ? ` (${parts.join(', ')})` : '';

            groupEl.innerHTML = `
              <div class="qom-summary-line-header" data-qom-group-header>
                <span class="qom-summary-line-header-left">
                  <span class="badges-wrapper qom-summary-badge-wrapper">
                    <div class="product-card-badge" badge-label="${badgeLabel}"><p class="product-card-badge-label">${profileText}</p></div>
                  </span>
                  <span class="qom-summary-line-kg">${groupKg} kg${breakdown}</span>
                </span>
                <span style="display:flex;align-items:center;gap:8px;">
                  <span>${this.formatMoney(groupTotalDiscounted)}</span>
                  <svg class="qom-summary-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m6 9 6 6 6-6"/></svg>
                </span>
              </div>
              ${subRows}
            `;
            linesEl.appendChild(groupEl);
          });
        }
      }

      this.setText('[data-qom-total-kg]', `${totalKg} kg en total`);
      this.setText('[data-qom-subtotal]', this.formatMoney(subtotal));
      this.querySelectorAll('[data-qom-shipping]').forEach((shippingEl) => {
        if (shippingCost <= 0 && lineItems.length > 0) {
          shippingEl.textContent = 'GRATIS';
          shippingEl.classList.add('qom-shipping-free');
        } else {
          shippingEl.textContent = this.formatMoney(shippingCost);
          shippingEl.classList.remove('qom-shipping-free');
        }
      });
      this.setText('[data-qom-total]', `${this.formatMoney(total)} MXN`);

      const totalOriginal = subtotal + shippingCost;
      this.querySelectorAll('[data-qom-total-original]').forEach((el) => {
        el.hidden = discountAmount <= 0;
        el.textContent = this.formatMoney(totalOriginal);
      });
      this.setText('[data-qom-total-mobile]', this.formatMoney(total));

      this.setText('[data-qom-mobile-bar-kg]', `${totalKg} kg`);
      this.setText('[data-qom-mobile-bar-total]', this.formatMoney(total));

      this.querySelectorAll('[data-qom-discount-row]').forEach((el) => {
        el.hidden = discountAmount <= 0;
      });
      this.setText('[data-qom-discount-pct]', String(discountPercent));
      this.setText('[data-qom-discount-amount]', `\u2212${this.formatMoney(discountAmount)}`);

      const checkoutBtn = this.querySelector('[data-qom-checkout]');
      if (checkoutBtn) checkoutBtn.disabled = lineItems.length === 0;

      this._lineItems = lineItems;
      this.scheduleCartSync();
    }

    setText(selector, text) {
      this.querySelectorAll(selector).forEach((el) => {
        el.textContent = text;
      });
    }

    formatMoney(amount) {
      const isMobile = window.matchMedia('(max-width: 720px)').matches;
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: isMobile ? 0 : 2,
        maximumFractionDigits: isMobile ? 0 : 2,
      }).format(amount || 0);
    }

    buildCartUpdates() {
      const updates = {};
      this.rowEls.forEach((row) => {
        row.querySelectorAll('[data-qom-stepper]').forEach((stepper) => {
          const variantId = stepper.dataset.variantId;
          const qty = parseInt(stepper.querySelector('[data-qom-qty-input]').value, 10) || 0;
          if (variantId) updates[variantId] = qty;
        });
      });
      return updates;
    }

    scheduleCartSync() {
      if (this._syncTimer) clearTimeout(this._syncTimer);
      this._syncTimer = setTimeout(() => {
        this._syncTimer = null;
        this.syncCartToServer();
      }, 500);
    }

    async syncCartToServer() {
      const updates = this.buildCartUpdates();
      this._syncing = true;
      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates }),
        });
        if (!response.ok) throw new Error('cart update failed');
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      } catch (err) {
        this.showToast('No se pudo actualizar el carrito, intenta de nuevo.');
        console.error('[quick-order-mayoristas]', err);
      } finally {
        this._syncing = false;
      }
    }

    async addAllToCart() {
      const checkoutBtn = this.querySelector('[data-qom-checkout]');
      const originalText = checkoutBtn.textContent;
      checkoutBtn.disabled = true;
      checkoutBtn.textContent = 'Actualizando carrito...';

      try {
        if (this._syncTimer) {
          clearTimeout(this._syncTimer);
          this._syncTimer = null;
        }
        await this.syncCartToServer();
        window.location.href = this.checkoutUrl;
      } catch (err) {
        checkoutBtn.disabled = false;
        checkoutBtn.textContent = originalText;
        this.showToast('Hubo un problema actualizando tu carrito, intenta de nuevo.');
        console.error('[quick-order-mayoristas]', err);
      }
    }

    showToast(message) {
      const template = this.querySelector('[data-qom-toast-template]');
      if (!template) return;
      const clone = template.content.cloneNode(true);
      const toastEl = clone.querySelector('[data-qom-toast]');
      toastEl.textContent = message;
      document.body.appendChild(clone);
      setTimeout(() => {
        const el = document.querySelector('[data-qom-toast]');
        if (el) el.remove();
      }, 3200);
    }
  }

  if (!window.customElements.get(NAME)) {
    window.customElements.define(NAME, QuickOrderMayoristas);
  }
})();
